import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const CONCRETE = "https://images.unsplash.com/photo-1630288214173-a119cf823388?fm=jpg&q=80&w=1600&auto=format&fit=crop";

const EASE = [0.76, 0, 0.24, 1];

function Line({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.12em]">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  const scrollDown = () => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <div
          className="absolute right-0 top-0 h-full w-full md:w-[52%]"
          style={{
            backgroundImage: `url(${CONCRETE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.4) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 md:via-[#050505]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1500px] px-5 md:px-10 min-h-screen flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-8 h-px bg-[#ff4500]" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ff4500]">
            NABL Accredited Laboratory
          </span>
        </motion.div>

        <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.86] text-[10vw] sm:text-[9vw] md:text-[6.5vw] lg:text-[5.5rem] text-[#f4f4f5]">
          <Line delay={0.35}>Qualiz Material</Line>
          <Line delay={0.45}>
            <span className="text-[#ff4500]">Testing</span> & Research
          </Line>
          <Line delay={0.55}>Centre Pvt. Ltd.</Line>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="max-w-md text-[#8e8e93] text-base md:text-lg font-light leading-relaxed"
          >
            Qualiz Material Testing & Research Centre, Akola — your trusted partner in soil and
            construction material testing for resilient infrastructure.
          </motion.p>

          <motion.button
            data-testid="hero-explore-cta"
            onClick={scrollDown}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="group flex items-center gap-4 font-mono text-[11px] tracking-[0.25em] uppercase text-[#f4f4f5]"
          >
            <span className="w-12 h-12 border border-[#27272a] flex items-center justify-center group-hover:bg-[#ff4500] group-hover:border-[#ff4500] transition-colors duration-400">
              <ArrowDown size={18} className="group-hover:text-[#050505] transition-colors duration-400 animate-bounce" />
            </span>
            Explore Services
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
